import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { Player, Categories, WordData, P2PMessage } from '@/types'
import { useGameConfigStore } from '@/stores/gameConfig'
import { usePlayerStore } from '@/stores/player'
import { useUIStore } from '@/stores/ui'

interface UseGameParams {
  send: (message: P2PMessage) => void
  myPlayerId: Ref<string | null>
}

export function useGame({ send, myPlayerId }: UseGameParams) {
  const configStore = useGameConfigStore()
  const playerStore = usePlayerStore()
  const uiStore = useUIStore()

  // Estado local del juego (no en stores)
  const gameStarted: Ref<boolean> = ref(false)
  const currentRound: Ref<number> = ref(0)
  const totalRounds: Ref<number> = ref(0)
  const currentPlayerIndex: Ref<number> = ref(0)
  const isMyTurn: Ref<boolean> = ref(false)
  const timeLeft: Ref<number> = ref(0)
  const currentCategory: Ref<string> = ref('')
  const currentWord: Ref<string> = ref('')
  const usedWords: Ref<Set<string>> = ref(new Set())
  const rankings: Ref<Player[]> = ref([])

  let timerInterval: number | null = null

  // Getters
  const currentPlayer: ComputedRef<Player | null> = computed(() => {
    if (currentPlayerIndex.value >= playerStore.players.length) return null
    return playerStore.players[currentPlayerIndex.value] || null
  })

  // Actions (lógica de negocio)
  const startTimer = (): void => {
    if (timerInterval) clearInterval(timerInterval)

    timerInterval = window.setInterval(() => {
      timeLeft.value--
      if (timeLeft.value <= 0) {
        stopTimer()
        nextTurn()
      }
    }, 1000)
  }

  const stopTimer = (): void => {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }

  const selectRandomWord = (): void => {
    const wordsData: WordData[] = configStore.categories[currentCategory.value]?.words?.[configStore.options.difficulty] || []
    const available = wordsData.filter((w: WordData) => !usedWords.value.has(w.word))

    if (available.length === 0) {
      const keys = Object.keys(configStore.categories)
      let foundCategory: string | null = null
      let foundWords: WordData[] = []

      for (const key of keys) {
        const catWords = configStore.categories[key]?.words?.[configStore.options.difficulty] || []
        const catAvailable = catWords.filter((w: WordData) => !usedWords.value.has(w.word))
        if (catAvailable.length > 0) {
          foundCategory = key
          foundWords = catAvailable
          break
        }
      }

      if (!foundCategory) {
        stopTimer()
        const finalRankings = [...playerStore.players].sort((a, b) => b.score - a.score)
        rankings.value = finalRankings
        return
      }

      currentCategory.value = foundCategory
      const selected = foundWords[Math.floor(Math.random() * foundWords.length)]
      currentWord.value = selected.word
      usedWords.value.add(selected.word)
      return
    }

    const selected = available[Math.floor(Math.random() * available.length)]
    currentWord.value = selected.word
    usedWords.value.add(selected.word)
  }

  const startTurn = (): void => {
    const player = currentPlayer.value
    if (!player) {
      endGame()
      return
    }

    // Seleccionar categoría si no está seleccionada
    if (!currentCategory.value) {
      // Si hay categorías seleccionadas (ruleta/manual), usar la primera
      if (configStore.options.selectedCategories.length > 0) {
        currentCategory.value = configStore.options.selectedCategories[0]
      } else {
        // Ruleta: seleccionar aleatoria de todas las categorías
        const keys = Object.keys(configStore.categories)
        currentCategory.value = keys[Math.floor(Math.random() * keys.length)]
      }
    }

    isMyTurn.value = player.id === myPlayerId.value
    timeLeft.value = configStore.options.timerDuration

    selectRandomWord()

    // Enviar mensaje P2P
    send({
      type: 'next-turn',
      playerId: player.id,
      playerName: player.name,
      category: currentCategory.value,
      word: currentWord.value
    } as P2PMessage)

    if (isMyTurn.value && navigator.vibrate) {
      navigator.vibrate(0)
      navigator.vibrate([200, 100, 200])
    }

    startTimer()
  }

  const nextTurn = (): void => {
    stopTimer()
    currentPlayerIndex.value++

    if (currentPlayerIndex.value >= playerStore.players.length) {
      currentRound.value++
      if (currentRound.value > configStore.options.roundsPerPlayer) {
        endGame()
        return
      }
      currentPlayerIndex.value = 0
    }

    // Mantener la categoría seleccionada (ruleta/manual) durante toda la partida
    // currentCategory.value = ''  // No limpiar
    startTurn()
  }

  const correctAnswer = (): void => {
    const player = currentPlayer.value
    if (player && player.id === myPlayerId.value) {
      player.score++
      // Actualizar store
      playerStore.updatePlayerScore(player.id, player.score)

      send({
        type: 'score-update',
        playerId: player.id,
        score: player.score
      } as P2PMessage)
    }

    send({
      type: 'correct-answer',
      playerId: myPlayerId.value
    } as P2PMessage)

    nextTurn()
  }

  const skipWord = (): void => {
    selectRandomWord()
    send({
      type: 'skip-word',
      category: currentCategory.value,
      word: currentWord.value
    } as P2PMessage)
  }

  const startGame = (): void => {
    currentCategory.value = ''
    usedWords.value = new Set()
    currentRound.value = 1
    totalRounds.value = configStore.options.roundsPerPlayer
    currentPlayerIndex.value = 0
    gameStarted.value = true

    send({
      type: 'game-start',
      players: playerStore.players,
      options: configStore.options
    } as P2PMessage)

    uiStore.navigateTo('game')
    startTurn()
  }

  const endGame = (): void => {
    stopTimer()
    const finalRankings = [...playerStore.players].sort((a, b) => b.score - a.score)
    rankings.value = finalRankings
    gameStarted.value = false

    send({
      type: 'game-end',
      rankings: finalRankings
    } as P2PMessage)

    uiStore.navigateTo('results')
  }

  const handleP2PMessage = (message: P2PMessage): void => {
    switch (message.type) {
      case 'game-start':
        playerStore.syncPlayers(message.players)
        configStore.updateOptions(message.options)
        currentCategory.value = ''
        usedWords.value = new Set()
        currentRound.value = 1
        totalRounds.value = message.options.roundsPerPlayer
        currentPlayerIndex.value = 0
        gameStarted.value = true
        uiStore.navigateTo('game')
        break

      case 'next-turn':
        currentPlayerIndex.value = playerStore.players.findIndex(p => p.id === message.playerId)
        currentCategory.value = message.category
        currentWord.value = message.word
        isMyTurn.value = message.playerId === myPlayerId.value
        timeLeft.value = configStore.options.timerDuration
        if (isMyTurn.value && navigator.vibrate) {
          navigator.vibrate(0)
          navigator.vibrate([200, 100, 200])
        }
        startTimer()
        break

      case 'score-update':
        playerStore.updatePlayerScore(message.playerId, message.score)
        break

      case 'correct-answer':
        if (message.playerId !== myPlayerId.value) {
          nextTurn()
        }
        break

      case 'skip-word':
        currentCategory.value = message.category
        currentWord.value = message.word
        break

      case 'game-end':
        stopTimer()
        playerStore.syncPlayers(message.rankings)
        rankings.value = [...message.rankings]
        gameStarted.value = false
        uiStore.navigateTo('results')
        break
    }
  }

  return {
    gameStarted,
    currentRound,
    totalRounds,
    currentPlayerIndex,
    isMyTurn,
    timeLeft,
    currentCategory,
    currentWord,
    usedWords,
    rankings,
    currentPlayer,
    startTimer,
    stopTimer,
    selectRandomWord,
    startTurn,
    nextTurn,
    correctAnswer,
    skipWord,
    startGame,
    endGame,
    handleP2PMessage
  }
}
